import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _isNil from "lodash/isNil";
import _isArray from "lodash/isArray";
import _get from "lodash/get";
import { ENTITY_TYPES, PRODUCT_TYPES, STATUSES } from '../constants';
import * as autoLoansSelectors from './auto-loans';
import * as bankingSelectors from './banking';
import * as creditCardsSelectors from './credit-cards';
import * as educationLoansSelectors from './education-loans';
import * as insuranceSelectors from './insurance';
import * as investingSelectors from './investing';
import * as mortgagesSelectors from './mortgages';
import * as personalLoansSelectors from './personal-loans';
import * as smbLoansSelectors from './small-medium-business-loans';
import * as softwareSelectors from './software';
import { isLegacyHomeownershipEntity } from '../utils/mortgages';
import { makeModuleDeprecationDecorator } from '../utils/deprecation';
var withDeprecationWarning = makeModuleDeprecationDecorator('common');
/**
 * Get the product type of a MarketplaceEntity.
 *
 * @param  {Object} marketplaceEntity Marketplace entity.
 *
 * @return {string} Product type.
 */

export var getProductType = function getProductType(marketplaceEntity) {
  /**
   * The mortgages entity from Homeownership is a special snowflake.
   *
   * Note: `{ raw: true }` must be included in `POST` to Homeownership service to get `type`.
   */
  if (isLegacyHomeownershipEntity(marketplaceEntity)) {
    return PRODUCT_TYPES.MORTGAGES;
  }

  return _get(marketplaceEntity, 'product_type');
};
/**
 * Get the entity type of a MarketplaceEntity.
 *
 * @param {Object} marketplaceEntity Marketplace entity.
 *
 * @return {string} Entity type.
 */

export var getEntityType = function getEntityType(marketplaceEntity) {
  /**
   * The mortgages entity from Homeownership is a special snowflake.
   *
   * Note: `{ raw: true }` must be included in `POST` to Homeownership service to get `type`.
   */
  var entityType = _get(marketplaceEntity, 'entity_type');

  if (!entityType && isLegacyHomeownershipEntity(marketplaceEntity)) {
    return ENTITY_TYPES.PRODUCTS;
  }

  return entityType;
};
/**
 * Map a marketplaceEntity to a function by entity type and call that function
 *
 * @param {Object | Array} args Either the marketplace entity or an
 *     array of arguments to be passed to the selector function. The
 *     first element of the array must be the marketplace entity
 * @param {Object} mappings Map from entity type to function.
 * @return {any} The value of the called mapped function.
 */

export var callSelectorByEntityType = function callSelectorByEntityType(args) {
  var mappings = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};

  if (!_isArray(args)) {
    // eslint-disable-next-line no-param-reassign -- directive added automatically by Shepherd migration
    args = [args];
  }

  var _args = args,
      _args2 = _slicedToArray(_args, 1),
      marketplaceEntity = _args2[0];

  var entityType = getEntityType(marketplaceEntity);
  var func = mappings[entityType];

  if (!func) {
    return undefined;
  }

  return func.apply(void 0, _toConsumableArray(args));
};
/**
 * Map a marketplaceEntity to a function by product type and call that function
 *
 * @param      {Object | Array}  args               Either the marketplace entity or
 *                                                  an array of arguments to be passed to the
 *                                                  selector function. The first element of the
 *                                                  array must be the marketplace entity
 * @param      {Object}           mappings          Map from product type to function
 * @return     {any}  return value of called mapped function
 */

export var callSelectorByProductType = function callSelectorByProductType(args) {
  var mappings = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  var defaultFunc = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : function () {
    return undefined;
  };

  if (!_isArray(args)) {
    // eslint-disable-next-line no-param-reassign -- directive added automatically by Shepherd migration
    args = [args];
  }

  var _args3 = args,
      _args4 = _slicedToArray(_args3, 1),
      marketplaceEntity = _args4[0];

  var productType = getProductType(marketplaceEntity);
  var func = mappings[productType];

  if (!func) {
    return defaultFunc.apply(void 0, _toConsumableArray(args));
  }

  return func.apply(void 0, _toConsumableArray(args));
};
/**
 * Map a marketplaceEntity to a function by sub product type and call that function
 *
 * @param      {Object | Array}  args               Either the marketplace entity or
 *                                                  an array of arguments to be passed to the
 *                                                  selector function. The first element of the
 *                                                  array must be the marketplace entity
 * @param      {Object}           mappings          Map from sub product type to function
 * @return     {any}  return value of called mapped function
 */

export var callSelectorBySubProductType = function callSelectorBySubProductType(args) {
  var mappings = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};

  if (!_isArray(args)) {
    // eslint-disable-next-line no-param-reassign -- directive added automatically by Shepherd migration
    args = [args];
  }

  var _args5 = args,
      _args6 = _slicedToArray(_args5, 1),
      marketplaceEntity = _args6[0];

  var subProductType = getSubProductType(marketplaceEntity);
  var func = mappings[subProductType];
  if (!func) return undefined;
  return func.apply(void 0, _toConsumableArray(args));
};
/**
 * Gets Apply Now link url
 *
 * @param  {Object} marketplaceEntity The marketplace entity
 *
 * @return {String}                   Apply Now url
 */

export var getApplyLink = function getApplyLink(marketplaceEntity) {
  var _callSelectorByProduc;

  return callSelectorByProductType(marketplaceEntity, (_callSelectorByProduc = {}, _defineProperty(_callSelectorByProduc, PRODUCT_TYPES.AUTO_LOANS, autoLoansSelectors.getApplyLink), _defineProperty(_callSelectorByProduc, PRODUCT_TYPES.MORTGAGES, mortgagesSelectors.getApplyLink), _callSelectorByProduc), function (entity) {
    return _get(entity, 'derived_details.apply_link.value');
  });
};
/**
 * Get the max APR.
 * @note LOANS SPECIFIC.
 *
 * @param   {Object} marketplaceEntity  The marketplace entity.
 * @return  {number}                    The maximum APR.
 */

export var getAprMax = function getAprMax(marketplaceEntity) {
  var _callSelectorByProduc2;

  return callSelectorByProductType(marketplaceEntity, (_callSelectorByProduc2 = {}, _defineProperty(_callSelectorByProduc2, PRODUCT_TYPES.AUTO_LOANS, autoLoansSelectors.getAprMax), _defineProperty(_callSelectorByProduc2, PRODUCT_TYPES.PERSONAL_LOANS, personalLoansSelectors.getAprMax), _defineProperty(_callSelectorByProduc2, PRODUCT_TYPES.SMB_LOANS, smbLoansSelectors.getAprMax), _callSelectorByProduc2));
};
/**
 * Get the min APR.
 * @note LOANS SPECIFIC.
 *
 * @param   {Object} marketplaceEntity  The marketplace entity.
 * @return  {number}                    The minimum APR.
 */

export var getAprMin = function getAprMin(marketplaceEntity) {
  var _callSelectorByProduc3;

  return callSelectorByProductType(marketplaceEntity, (_callSelectorByProduc3 = {}, _defineProperty(_callSelectorByProduc3, PRODUCT_TYPES.AUTO_LOANS, autoLoansSelectors.getAprMin), _defineProperty(_callSelectorByProduc3, PRODUCT_TYPES.PERSONAL_LOANS, personalLoansSelectors.getAprMin), _defineProperty(_callSelectorByProduc3, PRODUCT_TYPES.SMB_LOANS, smbLoansSelectors.getAprMin), _callSelectorByProduc3));
};
/**
 * Get bottom line content
 *
 * @param  {Object} marketplaceEntity The marketplace entity
 *
 * @return {String}                   "Bottom line" text
 */

export var getBottomLine = function getBottomLine(marketplaceEntity) {
  var _callSelectorByProduc4;

  return callSelectorByProductType(marketplaceEntity, (_callSelectorByProduc4 = {}, _defineProperty(_callSelectorByProduc4, PRODUCT_TYPES.AUTO_LOANS, autoLoansSelectors.getBottomLine), _defineProperty(_callSelectorByProduc4, PRODUCT_TYPES.EDUCATION_LOANS, educationLoansSelectors.getBottomLine), _defineProperty(_callSelectorByProduc4, PRODUCT_TYPES.INVESTING, investingSelectors.getBottomLine), _defineProperty(_callSelectorByProduc4, PRODUCT_TYPES.MORTGAGES, mortgagesSelectors.getBottomLine), _defineProperty(_callSelectorByProduc4, PRODUCT_TYPES.PERSONAL_LOANS, personalLoansSelectors.getBottomLine), _defineProperty(_callSelectorByProduc4, PRODUCT_TYPES.SMB_LOANS, smbLoansSelectors.getBottomLine), _callSelectorByProduc4));
};
/**
 * Get the "cons" bullets for an offer.
 *
 * @param   {Object} marketplaceEntity  The marketplaceEntity
 * @return  {Array}                     The "cons" array
 */

export var getConsBullets = function getConsBullets(marketplaceEntity) {
  var _callSelectorByProduc5;

  return callSelectorByProductType(marketplaceEntity, (_callSelectorByProduc5 = {}, _defineProperty(_callSelectorByProduc5, PRODUCT_TYPES.AUTO_LOANS, autoLoansSelectors.getConsBullets), _defineProperty(_callSelectorByProduc5, PRODUCT_TYPES.CREDIT_CARDS, creditCardsSelectors.getConsBullets), _defineProperty(_callSelectorByProduc5, PRODUCT_TYPES.EDUCATION_LOANS, educationLoansSelectors.getConsBullets), _defineProperty(_callSelectorByProduc5, PRODUCT_TYPES.INVESTING, investingSelectors.getConsBullets), _defineProperty(_callSelectorByProduc5, PRODUCT_TYPES.MORTGAGES, mortgagesSelectors.getConsBullets), _defineProperty(_callSelectorByProduc5, PRODUCT_TYPES.PERSONAL_LOANS, personalLoansSelectors.getConsBullets), _defineProperty(_callSelectorByProduc5, PRODUCT_TYPES.SMB_LOANS, smbLoansSelectors.getConsBullets), _callSelectorByProduc5));
};
/**
 * Gets the created date of the entity
 *
 * @param {Object} marketplaceEntity The marketplace entity
 *
 * @return {number} Miliseconds since January 1, 1970
 */

export var getDateCreatedAt = function getDateCreatedAt(marketplaceEntity) {
  var secondsTimestamp = _get(marketplaceEntity, 'created_at');

  if (_isNil(secondsTimestamp)) return secondsTimestamp;
  return secondsTimestamp * 1000;
};
/**
 * Gets the last ingested date of the entity
 *
 * @param {Object} marketplaceEntity The marketplace entity
 *
 * @return {number} Miliseconds since January 1, 1970
 */

export var getDateLastIngestedAt = function getDateLastIngestedAt(marketplaceEntity) {
  var secondsTimestamp = _get(marketplaceEntity, 'details.last_ingested_at.value');

  if (_isNil(secondsTimestamp)) return secondsTimestamp;
  return secondsTimestamp * 1000;
};
/**
 * Gets the updated date of the entity
 *
 * @param {Object} marketplaceEntity The marketplace entity
 *
 * @return {number} Miliseconds since January 1, 1970
 */

export var getDateUpdatedAt = function getDateUpdatedAt(marketplaceEntity) {
  var secondsTimestamp = _get(marketplaceEntity, 'updated_at');

  if (_isNil(secondsTimestamp)) return secondsTimestamp;
  return secondsTimestamp * 1000;
};
/**
 * Gets flag to indicate if Apply links are disabled on editorial pages. When this is true, apply
 * links on editorial pages should be hidden or replaced with something else
 *
 * @param  {Object} marketplaceEntity The marketplace entity
 *
 * @return {boolean}                  disabled flag
 */

export var getDisableEditorialApplyLinkFlag = function getDisableEditorialApplyLinkFlag(marketplaceEntity) {
  return callSelectorByProductType(marketplaceEntity, _defineProperty({}, PRODUCT_TYPES.CREDIT_CARDS, creditCardsSelectors.getDisableEditorialApplyLinkFlag));
};
/**
 * Gets the disclaimer.
 *
 * @param   {Object} marketplaceEntity  The marketplace entity.
 * @return  {string}                    The disclaimer text blob.
 */

export var getDisclaimer = function getDisclaimer(marketplaceEntity) {
  var _callSelectorByProduc7;

  return callSelectorByProductType(marketplaceEntity, (_callSelectorByProduc7 = {}, _defineProperty(_callSelectorByProduc7, PRODUCT_TYPES.EDUCATION_LOANS, educationLoansSelectors.getDisclaimer), _defineProperty(_callSelectorByProduc7, PRODUCT_TYPES.PERSONAL_LOANS, personalLoansSelectors.getDisclaimer), _callSelectorByProduc7));
};
/**
 * Get the min FICO score.
 *
 * @param   {Object} marketplaceEntity  The marketplace entity.
 * @return  {number}                    The minimum FICO score (0 indicates no min score).
 */

export var getFicoMin = function getFicoMin(marketplaceEntity) {
  var _callSelectorByProduc8;

  return callSelectorByProductType(marketplaceEntity, (_callSelectorByProduc8 = {}, _defineProperty(_callSelectorByProduc8, PRODUCT_TYPES.AUTO_LOANS, autoLoansSelectors.getFicoMin), _defineProperty(_callSelectorByProduc8, PRODUCT_TYPES.CREDIT_CARDS, creditCardsSelectors.getMinimumFICO), _defineProperty(_callSelectorByProduc8, PRODUCT_TYPES.EDUCATION_LOANS, educationLoansSelectors.getMinFICO), _defineProperty(_callSelectorByProduc8, PRODUCT_TYPES.PERSONAL_LOANS, personalLoansSelectors.getFicoMin), _defineProperty(_callSelectorByProduc8, PRODUCT_TYPES.SMB_LOANS, smbLoansSelectors.getFicoMin), _callSelectorByProduc8));
};
/**
 * Get the ID.
 *
 * @param {Object} marketplaceEntity The marketplace entity.
 *
 * @return {string} The ID.
 */

export var getId = function getId(marketplaceEntity) {
  return _get(marketplaceEntity, 'id');
};
/**
 * Get the ancestor institution.
 *
 * @param {Object} marketplaceEntity The marketplace entity.
 *
 * @return {string} The ancestor institution.
 */

export var getInstitution = function getInstitution(marketplaceEntity) {
  var _callSelectorByEntity;

  return callSelectorByEntityType(marketplaceEntity, (_callSelectorByEntity = {}, _defineProperty(_callSelectorByEntity, ENTITY_TYPES.OFFERS, function () {
    return marketplaceEntity.product.institution;
  }), _defineProperty(_callSelectorByEntity, ENTITY_TYPES.PRODUCTS, function () {
    return marketplaceEntity.institution;
  }), _defineProperty(_callSelectorByEntity, ENTITY_TYPES.INSTITUTIONS, function () {
    return marketplaceEntity;
  }), _callSelectorByEntity));
};
/**
 * Gets the alt text for the image associated with the offer
 *
 * @param  {Object} marketplaceEntity The marketplace entity
 *
 * @return {string}                   Alt text
 */

export var getImageAltText = function getImageAltText(marketplaceEntity) {
  var _callSelectorByProduc9;

  return callSelectorByProductType(marketplaceEntity, (_callSelectorByProduc9 = {}, _defineProperty(_callSelectorByProduc9, PRODUCT_TYPES.AUTO_LOANS, autoLoansSelectors.getImageAltText), _defineProperty(_callSelectorByProduc9, PRODUCT_TYPES.BANKING, bankingSelectors.getImageAltText), _defineProperty(_callSelectorByProduc9, PRODUCT_TYPES.CREDIT_CARDS, creditCardsSelectors.getImageAltText), _defineProperty(_callSelectorByProduc9, PRODUCT_TYPES.EDUCATION_LOANS, educationLoansSelectors.getImageAltText), _defineProperty(_callSelectorByProduc9, PRODUCT_TYPES.INSURANCE, insuranceSelectors.getImageAltText), _defineProperty(_callSelectorByProduc9, PRODUCT_TYPES.INVESTING, investingSelectors.getImageAltText), _defineProperty(_callSelectorByProduc9, PRODUCT_TYPES.MORTGAGES, mortgagesSelectors.getImageAltText), _defineProperty(_callSelectorByProduc9, PRODUCT_TYPES.PERSONAL_LOANS, personalLoansSelectors.getImageAltText), _defineProperty(_callSelectorByProduc9, PRODUCT_TYPES.SMB_LOANS, smbLoansSelectors.getImageAltText), _defineProperty(_callSelectorByProduc9, PRODUCT_TYPES.SOFTWARE, softwareSelectors.getImageAltText), _callSelectorByProduc9));
};
/*
 * Get the source URL of the image associated with the offer.
 *
 * @param      {Object}  marketplaceEntity  The marketplace entity
 * @param      {string}  size               Desired size of image, from constants.IMAGE_SIZES
 * @return     {string}  The URL of the image.
 */

export var getImageSrc = function getImageSrc(marketplaceEntity, size) {
  var _callSelectorByProduc10;

  return callSelectorByProductType([marketplaceEntity, size], (_callSelectorByProduc10 = {}, _defineProperty(_callSelectorByProduc10, PRODUCT_TYPES.AUTO_LOANS, autoLoansSelectors.getImageSrc), _defineProperty(_callSelectorByProduc10, PRODUCT_TYPES.BANKING, bankingSelectors.getImageSrc), _defineProperty(_callSelectorByProduc10, PRODUCT_TYPES.CREDIT_CARDS, creditCardsSelectors.getImageSrc), _defineProperty(_callSelectorByProduc10, PRODUCT_TYPES.EDUCATION_LOANS, educationLoansSelectors.getImageSrc), _defineProperty(_callSelectorByProduc10, PRODUCT_TYPES.INSURANCE, insuranceSelectors.getImageSrc), _defineProperty(_callSelectorByProduc10, PRODUCT_TYPES.INVESTING, investingSelectors.getImageSrc), _defineProperty(_callSelectorByProduc10, PRODUCT_TYPES.MORTGAGES, mortgagesSelectors.getImageSrc), _defineProperty(_callSelectorByProduc10, PRODUCT_TYPES.PERSONAL_LOANS, personalLoansSelectors.getImageSrc), _defineProperty(_callSelectorByProduc10, PRODUCT_TYPES.SMB_LOANS, smbLoansSelectors.getImageSrc), _defineProperty(_callSelectorByProduc10, PRODUCT_TYPES.SOFTWARE, softwareSelectors.getImageSrc), _callSelectorByProduc10));
};
/**
 * Get whether or not the entity is monetizing.
 *
 * @param  {Object}  marketplaceEntity The marketplace entity.
 *
 * @return {boolean} If the entity is monetizing.
 */

export var getIsMonetizing = function getIsMonetizing(marketplaceEntity) {
  var _callSelectorByProduc11;

  return callSelectorByProductType(marketplaceEntity, (_callSelectorByProduc11 = {}, _defineProperty(_callSelectorByProduc11, PRODUCT_TYPES.MORTGAGES, mortgagesSelectors.getIsMonetizing), _defineProperty(_callSelectorByProduc11, PRODUCT_TYPES.BANKING, bankingSelectors.getIsMonetizing), _callSelectorByProduc11), function (entity) {
    return _get(entity, 'details.is_monetizing.value');
  });
};
/**
 * Get the issuer (to be used with the ApplyNow component).
 *
 * @param {Object} marketplaceEntity The marketplace entity.
 *
 * @return {string} Issuer name.
 */

export var getIssuerName = function getIssuerName(marketplaceEntity) {
  var _callSelectorByProduc12;

  return callSelectorByProductType(marketplaceEntity, (_callSelectorByProduc12 = {}, _defineProperty(_callSelectorByProduc12, PRODUCT_TYPES.AUTO_LOANS, autoLoansSelectors.getIssuerName), _defineProperty(_callSelectorByProduc12, PRODUCT_TYPES.BANKING, bankingSelectors.getIssuerName), _defineProperty(_callSelectorByProduc12, PRODUCT_TYPES.CREDIT_CARDS, creditCardsSelectors.getIssuerName), _defineProperty(_callSelectorByProduc12, PRODUCT_TYPES.EDUCATION_LOANS, educationLoansSelectors.getIssuerName), _defineProperty(_callSelectorByProduc12, PRODUCT_TYPES.INSURANCE, insuranceSelectors.getIssuerName), _defineProperty(_callSelectorByProduc12, PRODUCT_TYPES.INVESTING, investingSelectors.getIssuerName), _defineProperty(_callSelectorByProduc12, PRODUCT_TYPES.MORTGAGES, mortgagesSelectors.getIssuerName), _defineProperty(_callSelectorByProduc12, PRODUCT_TYPES.PERSONAL_LOANS, personalLoansSelectors.getIssuerName), _defineProperty(_callSelectorByProduc12, PRODUCT_TYPES.SMB_LOANS, smbLoansSelectors.getIssuerName), _defineProperty(_callSelectorByProduc12, PRODUCT_TYPES.SOFTWARE, softwareSelectors.getIssuerName), _callSelectorByProduc12));
};
/**
 * Get url to learn more page. Usually card details page for credit cards
 * or review page for other verticals
 *
 * @param {Object} marketplaceEntity The marketplace entity.
 *
 * @return {string} The get more link URL.
 */

export var getLearnMoreLink = function getLearnMoreLink(marketplaceEntity) {
  var _callSelectorByProduc13;

  return callSelectorByProductType(marketplaceEntity, (_callSelectorByProduc13 = {}, _defineProperty(_callSelectorByProduc13, PRODUCT_TYPES.AUTO_LOANS, autoLoansSelectors.getLearnMoreLink), _defineProperty(_callSelectorByProduc13, PRODUCT_TYPES.BANKING, bankingSelectors.getLearnMoreLink), _defineProperty(_callSelectorByProduc13, PRODUCT_TYPES.CREDIT_CARDS, creditCardsSelectors.getLearnMoreLink), _defineProperty(_callSelectorByProduc13, PRODUCT_TYPES.EDUCATION_LOANS, educationLoansSelectors.getLearnMoreLink), _defineProperty(_callSelectorByProduc13, PRODUCT_TYPES.INSURANCE, insuranceSelectors.getLearnMoreLink), _defineProperty(_callSelectorByProduc13, PRODUCT_TYPES.INVESTING, investingSelectors.getLearnMoreLink), _defineProperty(_callSelectorByProduc13, PRODUCT_TYPES.MORTGAGES, mortgagesSelectors.getLearnMoreLink), _defineProperty(_callSelectorByProduc13, PRODUCT_TYPES.PERSONAL_LOANS, personalLoansSelectors.getLearnMoreLink), _defineProperty(_callSelectorByProduc13, PRODUCT_TYPES.SMB_LOANS, smbLoansSelectors.getLearnMoreLink), _defineProperty(_callSelectorByProduc13, PRODUCT_TYPES.SOFTWARE, softwareSelectors.getLearnMoreLink), _callSelectorByProduc13));
};
/**
 * Gets the legacy ID.
 *
 * @param {Object} marketplaceEntity The marketplace entity.
 *
 * @return {string} The legacy ID.
 */

export var getLegacyId = function getLegacyId(marketplaceEntity) {
  var _callSelectorByProduc14;

  return callSelectorByProductType(marketplaceEntity, (_callSelectorByProduc14 = {}, _defineProperty(_callSelectorByProduc14, PRODUCT_TYPES.CREDIT_CARDS, creditCardsSelectors.getLegacyId), _defineProperty(_callSelectorByProduc14, PRODUCT_TYPES.PERSONAL_LOANS, personalLoansSelectors.getLegacyId), _defineProperty(_callSelectorByProduc14, PRODUCT_TYPES.SMB_LOANS, smbLoansSelectors.getLegacyId), _callSelectorByProduc14));
};
/**
 * Get the max loan amount.
 * @note LOANS SPECIFIC.
 *
 * @param   {Object} marketplaceEntity  The marketplace entity.
 * @return  {number}                    The maximum loan amount.
 */

export var getLoanAmountMax = function getLoanAmountMax(marketplaceEntity) {
  return _get(marketplaceEntity, 'details.max_loan_amount.value');
};
/**
 * Get the min loan amount.
 * @note LOANS SPECIFIC.
 *
 * @param   {Object} marketplaceEntity  The marketplace entity.
 * @return  {number}                    The minimum loan amount.
 */

export var getLoanAmountMin = function getLoanAmountMin(marketplaceEntity) {
  return _get(marketplaceEntity, 'details.min_loan_amount.value');
};
/**
 * Get the max loan term in months.
 * @note LOANS SPECIFIC.
 *
 * @param   {Object} marketplaceEntity  The marketplace entity.
 * @return  {number}                    The maximum loan term in months.
 */

export var getLoanTermMax = function getLoanTermMax(marketplaceEntity) {
  var _callSelectorByProduc15;

  return callSelectorByProductType(marketplaceEntity, (_callSelectorByProduc15 = {}, _defineProperty(_callSelectorByProduc15, PRODUCT_TYPES.PERSONAL_LOANS, personalLoansSelectors.getLoanTermMax), _defineProperty(_callSelectorByProduc15, PRODUCT_TYPES.SMB_LOANS, smbLoansSelectors.getLoanTermMax), _callSelectorByProduc15));
};
/**
 * Get the min loan term in months.
 * @note PERSONAL LOANS SPECIFIC.
 *
 * @param   {Object} marketplaceEntity  The marketplace entity.
 * @return  {number}                    The minimum loan term in months.
 */

export var getLoanTermMin = function getLoanTermMin(marketplaceEntity) {
  return callSelectorByProductType(marketplaceEntity, _defineProperty({}, PRODUCT_TYPES.PERSONAL_LOANS, personalLoansSelectors.getLoanTermMin));
};
/**
 * Get whether or not the entity is monetizing.
 *
 * @param  {Object}  marketplaceEntity The marketplace entity.
 *
 * @return {boolean} If the entity is monetizing.
 */

export var getMonetizable = function getMonetizable(marketplaceEntity) {
  return _get(marketplaceEntity, 'derived_details.monetizable.value');
};
/**
 * Get the name to display for an offer.
 * @deprecated since 1.66.0 - Use `getDisplayName` or vertical specific selectors.
 *
 * @param      {Object}  marketplaceEntity  The marketplace entity
 * @return     {string}  The offer name.
 */

export var getName = withDeprecationWarning(function (marketplaceEntity) {
  var _callSelectorByProduc17;

  return callSelectorByProductType(marketplaceEntity, (_callSelectorByProduc17 = {}, _defineProperty(_callSelectorByProduc17, PRODUCT_TYPES.AUTO_LOANS, autoLoansSelectors.getName), _defineProperty(_callSelectorByProduc17, PRODUCT_TYPES.BANKING, bankingSelectors.getName), _defineProperty(_callSelectorByProduc17, PRODUCT_TYPES.CREDIT_CARDS, creditCardsSelectors.getName), _defineProperty(_callSelectorByProduc17, PRODUCT_TYPES.MORTGAGES, mortgagesSelectors.getName), _defineProperty(_callSelectorByProduc17, PRODUCT_TYPES.EDUCATION_LOANS, educationLoansSelectors.getName), _defineProperty(_callSelectorByProduc17, PRODUCT_TYPES.INSURANCE, insuranceSelectors.getName), _defineProperty(_callSelectorByProduc17, PRODUCT_TYPES.INVESTING, investingSelectors.getName), _defineProperty(_callSelectorByProduc17, PRODUCT_TYPES.PERSONAL_LOANS, personalLoansSelectors.getName), _defineProperty(_callSelectorByProduc17, PRODUCT_TYPES.SMB_LOANS, smbLoansSelectors.getName), _defineProperty(_callSelectorByProduc17, PRODUCT_TYPES.SOFTWARE, softwareSelectors.getName), _callSelectorByProduc17));
}, {
  name: 'getName',
  migration: 'Use `getDisplayName` instead. See https://github.com/NerdWallet/nwjs-marketplace/pull/108#discussion_r467241999 for more context.',
  version: '1.66.0'
});
/**
 * Get the name to display for a marketplaceEntity.
 *
 * @param {Object} marketplaceEntity - The marketplace entity.
 * @return {string} - The offer name.
 */

export var getDisplayName = function getDisplayName(marketplaceEntity) {
  var _callSelectorByProduc18;

  return callSelectorByProductType(marketplaceEntity, (_callSelectorByProduc18 = {}, _defineProperty(_callSelectorByProduc18, PRODUCT_TYPES.AUTO_LOANS, autoLoansSelectors.getName), _defineProperty(_callSelectorByProduc18, PRODUCT_TYPES.BANKING, getProductName), _defineProperty(_callSelectorByProduc18, PRODUCT_TYPES.CREDIT_CARDS, creditCardsSelectors.getName), _defineProperty(_callSelectorByProduc18, PRODUCT_TYPES.MORTGAGES, mortgagesSelectors.getName), _defineProperty(_callSelectorByProduc18, PRODUCT_TYPES.EDUCATION_LOANS, educationLoansSelectors.getName), _defineProperty(_callSelectorByProduc18, PRODUCT_TYPES.INSURANCE, insuranceSelectors.getName), _defineProperty(_callSelectorByProduc18, PRODUCT_TYPES.INVESTING, investingSelectors.getName), _defineProperty(_callSelectorByProduc18, PRODUCT_TYPES.PERSONAL_LOANS, personalLoansSelectors.getName), _defineProperty(_callSelectorByProduc18, PRODUCT_TYPES.SMB_LOANS, smbLoansSelectors.getName), _defineProperty(_callSelectorByProduc18, PRODUCT_TYPES.SOFTWARE, softwareSelectors.getName), _callSelectorByProduc18));
};
/**
 * Gets the offer if it exists.
 *
 * @param {Object} marketplaceEntity The marketplace entity.
 *
 * @return {string} The offer.
 */

export var getOffer = function getOffer(marketplaceEntity) {
  return callSelectorByEntityType(marketplaceEntity, _defineProperty({}, ENTITY_TYPES.OFFERS, function () {
    return marketplaceEntity;
  }));
};
/**
 * Gets the ancestor product.
 *
 * @param {Object} marketplaceEntity The marketplace entity.
 *
 * @return {string} The ancestor product.
 */

export var getProduct = function getProduct(marketplaceEntity) {
  var _callSelectorByEntity3;

  return callSelectorByEntityType(marketplaceEntity, (_callSelectorByEntity3 = {}, _defineProperty(_callSelectorByEntity3, ENTITY_TYPES.OFFERS, function () {
    return marketplaceEntity.product;
  }), _defineProperty(_callSelectorByEntity3, ENTITY_TYPES.PRODUCTS, function () {
    return marketplaceEntity;
  }), _callSelectorByEntity3));
};
/**
 * Gets the ancestor product name.
 *
 * @param {Object} marketplaceEntity The marketplace entity.
 *
 * @return {string} The ancestor product name.
 */

export var getProductName = function getProductName(marketplaceEntity) {
  return callSelectorByProductType(marketplaceEntity, _defineProperty({}, PRODUCT_TYPES.BANKING, bankingSelectors.getProductName));
};
/**
 * Get the "pros" bullets for an offer.
 *
 * @param   {Object} marketplaceEntity  The marketplaceEntity
 * @return  {Array}                     The "pros" array
 */

export var getProsBullets = function getProsBullets(marketplaceEntity) {
  var _callSelectorByProduc20;

  return callSelectorByProductType(marketplaceEntity, (_callSelectorByProduc20 = {}, _defineProperty(_callSelectorByProduc20, PRODUCT_TYPES.AUTO_LOANS, autoLoansSelectors.getProsBullets), _defineProperty(_callSelectorByProduc20, PRODUCT_TYPES.CREDIT_CARDS, creditCardsSelectors.getProsBullets), _defineProperty(_callSelectorByProduc20, PRODUCT_TYPES.EDUCATION_LOANS, educationLoansSelectors.getProsBullets), _defineProperty(_callSelectorByProduc20, PRODUCT_TYPES.INVESTING, investingSelectors.getProsBullets), _defineProperty(_callSelectorByProduc20, PRODUCT_TYPES.MORTGAGES, mortgagesSelectors.getProsBullets), _defineProperty(_callSelectorByProduc20, PRODUCT_TYPES.PERSONAL_LOANS, personalLoansSelectors.getProsBullets), _defineProperty(_callSelectorByProduc20, PRODUCT_TYPES.SMB_LOANS, smbLoansSelectors.getProsBullets), _callSelectorByProduc20));
};
/**
 * Get the qualifications bullets.
 *
 * @param   {Object} marketplaceEntity  The marketplace entity.
 * @return  {Array}                     The qualifications bullets.
 */

export var getQualificationsBullets = function getQualificationsBullets(marketplaceEntity) {
  var _callSelectorByProduc21;

  return callSelectorByProductType(marketplaceEntity, (_callSelectorByProduc21 = {}, _defineProperty(_callSelectorByProduc21, PRODUCT_TYPES.AUTO_LOANS, autoLoansSelectors.getQualificationsBullets), _defineProperty(_callSelectorByProduc21, PRODUCT_TYPES.EDUCATION_LOANS, educationLoansSelectors.getQualificationsBullets), _defineProperty(_callSelectorByProduc21, PRODUCT_TYPES.PERSONAL_LOANS, personalLoansSelectors.getQualificationsBullets), _defineProperty(_callSelectorByProduc21, PRODUCT_TYPES.SMB_LOANS, smbLoansSelectors.getQualificationsBullets), _callSelectorByProduc21));
};
/**
 * Get the slug for an offer.
 *
 * @param      {Object}  marketplaceEntity  The marketplace entity
 * @return     {string}  The offer slug.
 */

export var getSlug = function getSlug(marketplaceEntity) {
  var _callSelectorByProduc22;

  return callSelectorByProductType(marketplaceEntity, (_callSelectorByProduc22 = {}, _defineProperty(_callSelectorByProduc22, PRODUCT_TYPES.CREDIT_CARDS, creditCardsSelectors.getSlug), _defineProperty(_callSelectorByProduc22, PRODUCT_TYPES.BANKING, bankingSelectors.getSlug), _defineProperty(_callSelectorByProduc22, PRODUCT_TYPES.EDUCATION_LOANS, educationLoansSelectors.getSlug), _defineProperty(_callSelectorByProduc22, PRODUCT_TYPES.INVESTING, investingSelectors.getSlug), _defineProperty(_callSelectorByProduc22, PRODUCT_TYPES.MORTGAGES, mortgagesSelectors.getSlug), _defineProperty(_callSelectorByProduc22, PRODUCT_TYPES.PERSONAL_LOANS, personalLoansSelectors.getSlug), _defineProperty(_callSelectorByProduc22, PRODUCT_TYPES.SMB_LOANS, smbLoansSelectors.getSlug), _callSelectorByProduc22));
};
/**
 * Get the status of the entity
 * NOTE: does not support `MORTG`
 *
 * @param  {Object} marketplaceEntity The marketplace entity
 *
 * @return {string}                   The status [ACTIVE,INACTIVE]
 */

export var getStatus = function getStatus(marketplaceEntity) {
  return callSelectorByProductType(marketplaceEntity, _defineProperty({}, PRODUCT_TYPES.BANKING, bankingSelectors.getDisplayStatus), function (entity) {
    return _get(entity, 'status');
  });
};
/**
 * Get the star rating.
 *
 * @param      {Object}  marketplaceEntity  The marketplace entity
 * @return     {number}  The star rating.
 */

export var getStarRating = function getStarRating(marketplaceEntity) {
  var _callSelectorByProduc24;

  return callSelectorByProductType(marketplaceEntity, (_callSelectorByProduc24 = {}, _defineProperty(_callSelectorByProduc24, PRODUCT_TYPES.AUTO_LOANS, autoLoansSelectors.getStarRating), _defineProperty(_callSelectorByProduc24, PRODUCT_TYPES.BANKING, bankingSelectors.getStarRating), _defineProperty(_callSelectorByProduc24, PRODUCT_TYPES.CREDIT_CARDS, creditCardsSelectors.getStarRating), _defineProperty(_callSelectorByProduc24, PRODUCT_TYPES.EDUCATION_LOANS, educationLoansSelectors.getStarRating), _defineProperty(_callSelectorByProduc24, PRODUCT_TYPES.INSURANCE, insuranceSelectors.getStarRating), _defineProperty(_callSelectorByProduc24, PRODUCT_TYPES.INVESTING, investingSelectors.getStarRating), _defineProperty(_callSelectorByProduc24, PRODUCT_TYPES.MORTGAGES, mortgagesSelectors.getStarRating), _defineProperty(_callSelectorByProduc24, PRODUCT_TYPES.PERSONAL_LOANS, personalLoansSelectors.getStarRating), _defineProperty(_callSelectorByProduc24, PRODUCT_TYPES.SMB_LOANS, smbLoansSelectors.getStarRating), _callSelectorByProduc24));
};
/**
 * Get the star rating tooltip.
 *
 * @param      {Object}  marketplaceEntity  The marketplace entity
 * @return     {string}  The star rating tooltip.
 */

export var getStarRatingTooltip = function getStarRatingTooltip(marketplaceEntity) {
  var _callSelectorByProduc25;

  return callSelectorByProductType(marketplaceEntity, (_callSelectorByProduc25 = {}, _defineProperty(_callSelectorByProduc25, PRODUCT_TYPES.AUTO_LOANS, autoLoansSelectors.getStarRatingTooltip), _defineProperty(_callSelectorByProduc25, PRODUCT_TYPES.BANKING, bankingSelectors.getStarRatingTooltip), _defineProperty(_callSelectorByProduc25, PRODUCT_TYPES.CREDIT_CARDS, creditCardsSelectors.getStarRatingTooltip), _defineProperty(_callSelectorByProduc25, PRODUCT_TYPES.EDUCATION_LOANS, educationLoansSelectors.getStarRatingTooltip), _defineProperty(_callSelectorByProduc25, PRODUCT_TYPES.INSURANCE, insuranceSelectors.getStarRatingTooltip), _defineProperty(_callSelectorByProduc25, PRODUCT_TYPES.INVESTING, investingSelectors.getStarRatingTooltip), _defineProperty(_callSelectorByProduc25, PRODUCT_TYPES.MORTGAGES, mortgagesSelectors.getStarRatingTooltip), _defineProperty(_callSelectorByProduc25, PRODUCT_TYPES.PERSONAL_LOANS, personalLoansSelectors.getStarRatingTooltip), _callSelectorByProduc25));
};
/**
 * Get the sub product type of a marketplace entity.
 *
 * @param {Object} marketplaceEntity The marketplace entity.
 *
 * @return {string} Sub product type.
 */

export var getSubProductType = function getSubProductType(marketplaceEntity) {
  var _callSelectorByProduc26;

  return callSelectorByProductType(marketplaceEntity, (_callSelectorByProduc26 = {}, _defineProperty(_callSelectorByProduc26, PRODUCT_TYPES.AUTO_LOANS, autoLoansSelectors.getSubProductType), _defineProperty(_callSelectorByProduc26, PRODUCT_TYPES.BANKING, bankingSelectors.getSubProductType), _defineProperty(_callSelectorByProduc26, PRODUCT_TYPES.INVESTING, investingSelectors.getSubProductType), _defineProperty(_callSelectorByProduc26, PRODUCT_TYPES.EDUCATION_LOANS, educationLoansSelectors.getSubProductType), _defineProperty(_callSelectorByProduc26, PRODUCT_TYPES.INSURANCE, insuranceSelectors.getSubProductType), _callSelectorByProduc26));
};
/**
 * Get the version ID.
 *
 * @param {Object} marketplaceEntity The marketplace entity.
 * @return {number} The version ID.
 */

export var getVersionId = function getVersionId(marketplaceEntity) {
  return _get(marketplaceEntity, 'version_id');
};
/**
 * Get the active status of the entity
 * Returns `true` if entity is active
 *
 * @param  {Object} marketplaceEntity The marketplace entity
 *
 * @return {boolean}
 */

export var isActive = function isActive(marketplaceEntity) {
  return getStatus(marketplaceEntity) === STATUSES.ACTIVE;
};
/**
 * Get the id of the institution from the entity
 * @param {Object} marketplaceEntity
 */

export var getInstitutionId = function getInstitutionId(marketplaceEntity) {
  return getId(getInstitution(marketplaceEntity));
};
/**
 * Get the canonical instititution type.
 * @private - Use `getInstitutionType` instead.
 *
 * @param {Object} marketplaceEntity - The marketplace enitty.
 * @return {String} - The canonical institution type.
 */

export var getCanonicalInstitutionType = function getCanonicalInstitutionType(marketplaceEntity) {
  return _get(getInstitution(marketplaceEntity), 'type');
};
/**
 * Get the type of institution.
 * Does not work for Mortgage lender post!
 * @see {@link https://github.com/NerdWallet/nwjs-marketplace/blob/c3bbfb32e6a85077db9f67ded299253e569651f9/src/constants.js#L240-L269}
 *
 * @param {Object} marketplaceEntity - The marketplace enitty.
 * @return {String} - The canonical institution type.
 */

export var getInstitutionType = function getInstitutionType(marketplaceEntity) {
  return callSelectorByProductType(marketplaceEntity, _defineProperty({}, PRODUCT_TYPES.BANKING, bankingSelectors.getInstitutionType), getCanonicalInstitutionType);
};
/**
 * Get the id of the product from the entity
 * @param {Object} marketplaceEntity
 */

export var getProductId = function getProductId(marketplaceEntity) {
  return getId(getProduct(marketplaceEntity));
};
/**
 * Get the id of the offer from the entity
 * @param {Object} marketplaceEntity
 */

export var getOfferId = function getOfferId(marketplaceEntity) {
  return getId(getOffer(marketplaceEntity));
};
/**
 * Get the name of the institution from the entity
 * @param {Object} marketplaceEntity
 *
 * @return {String} - The name of the institution.
 */

export var getInstitutionName = function getInstitutionName(marketplaceEntity) {
  return _get(getInstitution(marketplaceEntity), 'details.display_name.value');
};